import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import QueryString from 'query-string'

import generalSelectors from 'models/General/selectors'
import authActions from 'models/Authentication/actions'
import authSelectors from 'models/Authentication/selectors'
import { composeCognitoError, validateUsername } from 'models/Authentication/utils'
import profileInfoSelectors from 'models/ProfileInfo/selectors'

import { STRING, EVENT_TYPE } from 'resources'
import { APP_URLS } from 'resources/urls'

import { sendAnalyticsEvent } from 'services/analytics'
import { validateVerificationCode } from 'utils/validators'
import { parseToken } from 'utils/jwt'

import useSignInRedirect from 'hooks/useSignInRedirect'

import AppInformationText from 'components/AppInformationText'
import UnauthenticatedLayout, { UNAUTHENTICATED_BACKGROUND_KEYS } from 'components/UnauthenticatedLayout'
import ScribbleBackground from 'components/ScribbleBackground'
import ReklaimBackButton from 'components/ReklaimBackButton'

import {
  ContentContainer,
  Body,
  Title,
  Text,
  TextPurple,
  Form,
  FormGroup,
  SignInButton,
  VerifyCodeInput,
  ResendCodeButton,
} from './styles'

const RESEND_CODE_WAIT_MS = 10000 // 10s
const TOKEN_PARAMS = ['username', 'code']

const SignInCodeVerification = () => {
  const hasAutoSubmitted = useRef(false)
  const hasAutoResent = useRef(false)

  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const [handleSignInRedirect] = useSignInRedirect()

  const region = useSelector(generalSelectors.getRegion)
  const username = useSelector(authSelectors.getUsername)
  const authCodeUserAlias = useSelector(authSelectors.getAuthCodeUserAlias)
  const isSignedIn = useSelector(authSelectors.getIsAuthenticated)
  const isProfileFetchSuccess = useSelector(profileInfoSelectors.statusSuccess)

  const [verificationCode, setVerificationCode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [composedCognitoError, setComposedCognitoError] = useState('')
  const [canResendVerificationCode, setCanResendVerificationCode] = useState(true)
  const [sendVerificationCodeFailed, setSendVerificationCodeFailed] = useState(false)

  const { isValid: isValidUsername, isEmail } = useMemo(() => validateUsername(username, region), [username, region])

  const locationTokenData = useMemo(() => {
    if (!location.search) {
      return null
    }

    const { next } = QueryString.parse(location.search.slice(1))
    if (next) {
      location.state = { from: { pathname: next } }
    }

    const { token } = QueryString.parse(location.search.slice(1))
    const parsedToken = parseToken(token)
    if (!parsedToken || !TOKEN_PARAMS.every((param) => param in parsedToken)) {
      return null
    }
    const sanitizedCode = (parsedToken.code || '').replace(/\D/g, '')
    if (!validateVerificationCode(sanitizedCode) || !validateUsername(parsedToken.username, region).isValid) {
      return null
    }

    return {
      username: parsedToken.username,
      code: sanitizedCode,
      userAlias: parsedToken.userAlias,
    }
  }, [location, region])

  const locationCode = locationTokenData?.code || null
  const locationUsername = locationTokenData?.username || null
  const userAlias = locationTokenData?.userAlias || authCodeUserAlias

  useEffect(() => {
    sendAnalyticsEvent(EVENT_TYPE.logInView)
  }, [])

  useEffect(() => {
    if (locationUsername) {
      dispatch(authActions.setAuthUsername.success(locationUsername))
    }
  }, [dispatch, locationUsername])

  useEffect(() => {
    if (locationCode) {
      hasAutoSubmitted.current = false
    }
  }, [locationCode])

  useEffect(() => {
    if (!canResendVerificationCode) {
      const timeoutId = setTimeout(() => setCanResendVerificationCode(true), RESEND_CODE_WAIT_MS)
      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [canResendVerificationCode])

  useEffect(() => {
    if (!locationTokenData && (!username || !isValidUsername)) {
      dispatch(authActions.resetLogIn.success())
      history.replace(APP_URLS.SIGN_IN)
    }
  }, [dispatch, history, locationTokenData, username, isValidUsername])

  const handleLogInSuccess = useCallback(() => {
    const { pathname: nextPath, search } = location?.state?.from || {}
    if (nextPath) {
      const combinedDeeplinkPath = QueryString.stringifyUrl({
        url: nextPath,
        query: QueryString.parse(search),
      })
      history.push(combinedDeeplinkPath)
    } else if ([APP_URLS.SIGN_IN, APP_URLS.SIGN_IN_VERIFY_CODE].includes(location.pathname)) {
      // If the page is already navigated away by Branch/Deep link, do nothing
      handleSignInRedirect()
    }
  }, [history, location, handleSignInRedirect])

  useEffect(() => {
    if (isSignedIn && isProfileFetchSuccess) {
      handleLogInSuccess()
    }
  }, [isSignedIn, isProfileFetchSuccess, handleLogInSuccess])

  const isUsernameEmpty = !username
  const isVerificationCodeValid = validateVerificationCode(verificationCode)
  const isInvalid = isUsernameEmpty || !isVerificationCodeValid || !isValidUsername || !!composedCognitoError

  const handleVerificationCodeChange = useCallback((_code) => {
    setComposedCognitoError('')
    setVerificationCode(_code)
  }, [])

  const handleVerifyCode = useCallback(
    (code, type) => {
      setIsSubmitting(true)
      dispatch(
        authActions.confirmLogInUser.init(code, userAlias, (isSuccess, message) => {
          setIsSubmitting(false)
          if (isSuccess) {
            sendAnalyticsEvent(EVENT_TYPE.logInCodeSuccess, { type })
            // Log-in handling is done in the effect above
          } else {
            const resendMethod = isEmail ? 'email' : 'sms'
            const newComposedCognitoError = composeCognitoError(message, resendMethod)
            setComposedCognitoError(newComposedCognitoError)
            if (!hasAutoResent.current && newComposedCognitoError.includes('expired')) {
              hasAutoResent.current = true
              setVerificationCode('')
              sendAnalyticsEvent(EVENT_TYPE.logInCodeFailure, { type, message, resend: true })
              dispatch(
                authActions.logInUser.init(username, isEmail, (isResendSuccess, resendMessage) => {
                  if (!isResendSuccess) {
                    setSendVerificationCodeFailed(true)
                    setComposedCognitoError(composeCognitoError(resendMessage))
                  }
                }),
              )
            } else {
              sendAnalyticsEvent(EVENT_TYPE.logInCodeFailure, { type, message })
            }
          }
        }),
      )
    },
    [dispatch, username, isEmail, userAlias],
  )

  useEffect(() => {
    if (!locationCode || !username || hasAutoSubmitted.current) {
      return
    }
    if (validateVerificationCode(locationCode)) {
      handleVerificationCodeChange(locationCode)
      hasAutoSubmitted.current = true
      handleVerifyCode(locationCode, 'LINK')
    }
  }, [locationCode, username, handleVerificationCodeChange, handleVerifyCode])

  const handleResendCode = useCallback(() => {
    if (!canResendVerificationCode) {
      return
    }
    setCanResendVerificationCode(false)
    handleVerificationCodeChange('')
    sendAnalyticsEvent(EVENT_TYPE.logInCodeResend)
    dispatch(
      authActions.logInUser.init(username, isEmail, (isSuccess, message) => {
        if (!isSuccess) {
          setSendVerificationCodeFailed(true)
          setComposedCognitoError(composeCognitoError(message))
        }
      }),
    )
  }, [dispatch, canResendVerificationCode, username, isEmail, handleVerificationCodeChange])

  const handleSubmit = (event) => {
    event.preventDefault()
    if (isInvalid || isSubmitting) {
      return
    }
    handleVerifyCode(verificationCode, 'CODE')
  }

  return (
    <UnauthenticatedLayout backgroundKey={UNAUTHENTICATED_BACKGROUND_KEYS.peopleWithLogo}>
      <ScribbleBackground>
        <ReklaimBackButton />
        <ContentContainer>
          <Body>
            <Form onSubmit={handleSubmit}>
              <Title>{STRING.signInCodeVerification.title}</Title>
              <Text>{STRING.signInCodeVerification.message}</Text>
              <TextPurple>{username}</TextPurple>
              <FormGroup>
                <VerifyCodeInput
                  data-testid="verification-code-input"
                  invalid={!!composedCognitoError}
                  errorMessage={composedCognitoError}
                  onChange={handleVerificationCodeChange}
                  value={verificationCode}
                  autoFocus
                />
                <ResendCodeButton
                  type="button"
                  onClick={handleResendCode}
                  disabled={!canResendVerificationCode || isSubmitting}
                >
                  {canResendVerificationCode && STRING.signInCodeVerification.resendButton}
                  {!canResendVerificationCode &&
                    (sendVerificationCodeFailed
                      ? STRING.signInCodeVerification.resendFailed
                      : STRING.signInCodeVerification.resendSent)}
                </ResendCodeButton>
              </FormGroup>
              <SignInButton
                buttonText={STRING.signInPage.loginButtonText}
                isLoading={isSubmitting}
                onPress={handleSubmit}
                disabled={isInvalid || isSubmitting}
                type="submit"
                data-testid="submit-button"
              />
            </Form>
          </Body>
          <AppInformationText />
        </ContentContainer>
      </ScribbleBackground>
    </UnauthenticatedLayout>
  )
}

export default SignInCodeVerification
